(() => {
  const seen = new WeakMap();
  const selectionSeen = new Set();
  let pendingAccount = null;
  let frame = 0;

  const fit = () => window.FAFFitProfile?.read?.() || {};
  const flags = (profile = fit()) => ({
    has_saved_footwear_size: Boolean(String(profile.womensFootwearSize || profile.mensFootwearSize || '').trim()),
    has_saved_apparel_size: Boolean(String(profile.womensApparelNumeric || profile.womensApparelAlpha || profile.womensBottomsNumeric || profile.mensApparelAlpha || '').trim()),
    has_calf_measurement: Number(profile.calfCircumference) > 0,
  });
  const publish = (name, data = {}) => {
    const properties = {
      ...data,
      ...flags(),
      faf_schema_version: '2',
      country: window.FAFThemeConfig?.countryCode || '',
      page_type: window.FAFThemeConfig?.pageType || 'storefront',
      page_path: window.location.pathname,
    };
    const event = `faf_${name}`;
    if (window.FAFAnalytics?.custom) window.FAFAnalytics.custom(event, properties);
    else {
      window.ElevarDataLayer = window.ElevarDataLayer ?? [];
      window.ElevarDataLayer.push({ event, event_properties: properties });
    }
    if (window.Shopify?.analytics?.publish) {
      Promise.resolve(window.Shopify.analytics.publish(`faf:${name}`, properties)).catch(() => {});
    }
  };
  const once = (node, key, name, data) => {
    if (!node || !key) return;
    const keys = seen.get(node) || new Set();
    if (keys.has(key)) return;
    keys.add(key);
    seen.set(node, keys);
    publish(name, data);
  };
  const product = (node) => {
    const config = window.FAFAnalytics?.readProduct?.(node) || {};
    const card = node?.closest?.('[data-elevar-product-card]');
    return {
      product_id: String(config.productId || ''),
      product_handle: String(card?.dataset?.productHandle || ''),
    };
  };
  const names = (node) => {
    try {
      const value = JSON.parse(node?.dataset?.fitNames || '[]');
      return Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean) : [];
    } catch (_) { return []; }
  };
  const visible = (node) => {
    if (!node?.isConnected) return false;
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  };

  const scan = (root = document) => {
    root.querySelectorAll?.('[data-pdp-fit-suggest]').forEach((section) => {
      if (!visible(section)) return;
      const data = window.Alpine?.$data?.(section);
      if (typeof data?.result !== 'function') return;
      const result = data.result();
      if (!result?.target) return;
      const configNode = section.querySelector("script[id^='PdpFitConfig-']");
      let config = {};
      try { config = JSON.parse(configNode?.textContent || '{}'); } catch (_) {}
      const variantId = String(result.target?.input?.value || '');
      const size = String(data.label?.(result.target) || '').trim();
      if (!variantId || !size) return;
      once(section, `fit:${result.status}:${variantId}:${Boolean(result.adjusted)}`, 'fit_recommendation_viewed', {
        surface: 'pdp',
        product_id: String(config.productId || ''),
        profile_key: String(data.profileKey || config.profileKey || ''),
        recommendation_status: String(result.status || ''),
        recommended_variant_id: variantId,
        recommended_size: size,
        adjusted: Boolean(result.adjusted),
        fit_direction: Number(config.fitDirection || 0),
        fit_reason: String(config.fitReason || ''),
        selected_variant_id: String(data.selectedVariantId || ''),
      });
    });

    root.querySelectorAll?.('[data-current-calf-fit]').forEach((section) => {
      if (!visible(section)) return;
      const data = window.Alpine?.$data?.(section);
      const rec = data?.calfRecommendation;
      if (rec?.status !== 'matched' || !rec.fit) return;
      once(section, `calf:pdp:${rec.fit}`, 'calf_fit_recommendation_viewed', {
        surface: 'pdp',
        product_id: String(window.currentProduct?.id || ''),
        current_fit: String(section.dataset.currentCalfFit || ''),
        recommended_fit: String(rec.fit || ''),
        recommendation_status: 'matched',
      });
    });

    root.querySelectorAll?.('[data-fit-guide][data-fit-names]').forEach((picker) => {
      if (!visible(picker)) return;
      const data = window.Alpine?.$data?.(picker);
      const recommended = String(data?.recommendedFit || '').trim();
      if (!recommended) return;
      once(picker, `calf:card:${recommended}`, 'calf_fit_recommendation_viewed', {
        surface: 'product_card',
        ...product(picker),
        current_fit: names(picker)[0] || '',
        recommended_fit: recommended,
        recommendation_status: 'matched',
      });
    });

    root.querySelectorAll?.('[data-profile-key]').forEach((status) => {
      if (!status.closest('[data-elevar-product-card]') || !visible(status)) return;
      const data = window.Alpine?.$data?.(status);
      if (!data?.available) return;
      once(status, `saved:${String(data.label || '')}`, 'saved_size_available_viewed', {
        surface: 'product_card',
        ...product(status),
        profile_key: String(status.dataset.profileKey || ''),
        displayed_size: String(data.label || ''),
      });
    });
  };

  const schedule = (root = document) => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => scan(root));
  };

  document.addEventListener('change', (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input?.matches('[data-variant-selector] input[data-pdp-product-id]')) return;
    window.requestAnimationFrame(() => {
      const section = input.closest('[data-pdp-fit-suggest]') || document.querySelector('[data-pdp-fit-suggest]');
      const data = window.Alpine?.$data?.(section);
      const result = typeof data?.result === 'function' ? data.result() : null;
      const recommendedId = String(result?.target?.input?.value || '');
      if (!recommendedId || recommendedId !== String(input.value || '')) return;
      const key = `${input.dataset.pdpProductId}:${recommendedId}`;
      if (selectionSeen.has(key)) return;
      selectionSeen.add(key);
      publish('fit_recommendation_selected', {
        surface: 'pdp',
        product_id: String(input.dataset.pdpProductId || ''),
        profile_key: String(data.profileKey || ''),
        selected_variant_id: recommendedId,
        recommended_variant_id: recommendedId,
        selected_size: String(data.label?.(result.target) || '').trim(),
        recommended_size: String(data.label?.(result.target) || '').trim(),
        recommendation_status: String(result.status || ''),
        adjusted: Boolean(result.adjusted),
        matches_recommendation: true,
        selection_method: 'variant_selector',
      });
    });
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const pdpCalf = target.closest('[data-current-calf-fit]');
    const pdpButton = target.closest('button');
    if (pdpCalf && pdpButton) {
      const data = window.Alpine?.$data?.(pdpCalf);
      const rec = data?.calfRecommendation;
      publish('fit_preferences_opened', {
        surface: 'pdp',
        product_id: String(window.currentProduct?.id || ''),
        field: rec?.status === 'missing-size' ? 'size' : 'calf',
        recommendation_status: String(rec?.status || ''),
        recommended_fit: String(rec?.fit || ''),
      });
    }

    const pdpOption = target.closest('[data-current-calf-fit] [data-calf-fit-option]');
    const pdpLink = target.closest('[data-current-calf-fit] [data-calf-fit-option] a[href]');
    if (pdpOption && pdpLink) {
      const section = pdpOption.closest('[data-current-calf-fit]');
      const data = window.Alpine?.$data?.(section);
      const selected = String(pdpOption.dataset.fitName || '').trim();
      const recommended = String(data?.calfRecommendation?.fit || '').trim();
      publish('calf_fit_selected', {
        surface: 'pdp',
        product_id: String(window.currentProduct?.id || ''),
        current_fit: String(section?.dataset.currentCalfFit || ''),
        selected_fit: selected,
        recommended_fit: recommended,
        matches_recommendation: Boolean(recommended && selected === recommended),
      });
    }

    const picker = target.closest('[data-fit-guide][data-fit-names]');
    if (picker) {
      const button = target.closest('button[aria-haspopup="true"]');
      const data = window.Alpine?.$data?.(picker);
      if (button && button.getAttribute('aria-expanded') !== 'true') {
        publish('calf_fit_picker_opened', {
          surface: 'product_card',
          ...product(picker),
          current_fit: names(picker)[0] || '',
          recommended_fit: String(data?.recommendedFit || ''),
        });
      }
      const link = target.closest('a[href*="/products/"]');
      if (link) {
        const selected = String(link.querySelector(':scope > span')?.textContent || '').trim();
        const selectedFit = selected ? `${selected} Fit` : '';
        const recommended = String(data?.recommendedFit || '').trim();
        publish('calf_fit_selected', {
          surface: 'product_card',
          ...product(picker),
          current_fit: names(picker)[0] || '',
          selected_fit: selectedFit,
          recommended_fit: recommended,
          matches_recommendation: Boolean(recommended && selectedFit === recommended),
        });
      }
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form?.matches('.push-account-fit-preferences form')) return;
    const before = fit();
    pendingAccount = {
      calf: String(before.calfCircumference || ''),
      system: String(before.preferredSizeSystem || ''),
      account_backed: Boolean(window.FAFFitProfile?.isAccountBacked?.()),
      started_at: Date.now(),
    };
  }, true);

  window.addEventListener('faf:fit-profile-changed', () => {
    if (pendingAccount) {
      const after = fit();
      const fresh = Date.now() - pendingAccount.started_at < 15000;
      const changed = [];
      if (pendingAccount.calf !== String(after.calfCircumference || '')) changed.push('calfCircumference');
      if (pendingAccount.system !== String(after.preferredSizeSystem || '')) changed.push('preferredSizeSystem');
      if (fresh && changed.length) {
        publish('fit_profile_updated', {
          surface: 'account',
          changed_fields: changed,
          changed_fields_csv: changed.join(','),
          changed_field_count: changed.length,
          account_backed: pendingAccount.account_backed,
        });
      }
      pendingAccount = null;
    }
    schedule();
  });

  ['faf:size-preference-changed', 'variant-change', 'load-more'].forEach((name) => {
    window.addEventListener(name, () => schedule());
  });
  window.addEventListener('lazy-panel:ready', (event) => schedule(event.detail?.root || document));
  window.addEventListener('faf:track-product-lists', (event) => schedule(event.detail?.root || document));
  document.addEventListener('alpine:initialized', () => schedule());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => schedule(), { once: true });
  else schedule();
})(); 
